import { defineComponent, PropType } from "vue";
import StepBox from "../../components/StepBox";
import FormInfo from "../../components/FormInfo";
import InputText from "../../forms/InputText";
import InputCheckbox from "../../forms/InputCheckbox";
import Utils from "../../utils/Utils";
import Expander from "../../components/Expander";
import BlueInfoBox from "../../components/BlueInfoBox";
import Captcha from "../../utils/Captcha";
import DocLink from "../../components/DocLink";
import { OfferTerm } from "../../utils/OfferUtils";
import Tracking from "../../utils/Tracking";
import { SmeOrderJson } from "../../utils/SmeUtils";

export default defineComponent({
    components: {
        InputText,
        InputCheckbox,
        StepBox,
        FormInfo,
        Expander,
        DocLink,
        BlueInfoBox,
    },

    template: `
        <StepBox class="StepSmeFormSummary StepFormSummary"
            :step="4"
            :currentstep="step"
            title="Umowa"

            :valid="valid"
            :back="false"
            :error="sent ? options.valid_summary_error : null"

            label_next="Przejdź do podpisu"
            sticky="auto"

            @back="$emit('goto', 3)"
            @next="onNext"
        >
            <h3 class="StepSmeFormSummary__title1">Podsumowanie</h3>
            <FormInfo title="Oferta" :value="options.offer.title" />
            <FormInfo title="Okres obowiązywania" :value="pricewarrantytitle" />

            <FormInfo v-if="options.offer?.cashback" title="Bonus na start" :value="priceCashback" />

            <template v-if="options.offer?.prices.length == 1">
                <FormInfo title="Cena za kWh" :value="priceOffer1" />
            </template>

            <template v-if="options.offer?.prices.length == 2">
                <FormInfo v-if="options.tariff == 'C12a'" title="Cena za kWh w szczycie" :value="priceOffer1" />
                <FormInfo v-if="options.tariff == 'C12a'" title="Cena za kWh poza szczytem" :value="priceOffer2" />
                <FormInfo v-if="options.tariff == 'C12b'" title="Cena za kWh dzienna" :value="priceOffer1" />
                <FormInfo v-if="options.tariff == 'C12b'" title="Cena za kWh nocna" :value="priceOffer2" />
            </template>

            <FormInfo title="Opłata handlowa" :value="priceFee" />
            <p v-if="!options.sme" class="StepFormSummary__feelegend">Opłata handlowa z uwzględnieniem rabatów za e-fakturę i zgody marketingowe</p>

            <FormInfo title="Deklarowane zużycie" :value="usage" />
            <FormInfo v-if="!options.sme" title="Długość umowy" :value="offerperiod" />
            <FormInfo title="Okres wypowiedzenia" :value="noticeperiod" />

            <hr>

            <h3>Podpisz dwa dokumenty i gotowe!</h3>
            <h4>Aby wszystko przebiegło sprawnie, do podpisania masz dwa dokumenty:</h4>

            <div class="StepFormSummary__maindocs">
                <DocLink v-if="contractterm" :title="contractterm.title" :url="contractterm.url" />

                <ul>
                    <li>Warunki handlowe dla miejsca dostarczania energii</li>
                    <li>Ogólne warunki Umowy</li>
                    <li>Oświadczenie Klienta o wyborze Sprzedawcy Rezerwowego</li>
                    <li>Zasady przetwarzania danych osobowych i danych pomiarowych</li>
                    <li>Regulamin produktu</li>
                    <li>Regulaminy promocji</li>
                </ul>

                <DocLink title="Pełnomocnictwo" url="/files/dokumenty/sme/2025/SME.Pelnomocnictwo.300125.pdf" />
            </div>

            <BlueInfoBox class="BlueInfoBox--summary" icon="Info">
                <p>W następnym kroku otrzymasz od nas dwa dokumenty do podpisania. Będą to:</p>

                <ol>
                    <li>Umowa</li>
                    <li>Pełnomocnictwo, dzięki któremu załatwimy za Ciebie wszystkie formalności.</li>
                </ol>

                <p>Czekaj na <b>dwa SMS-y z kodami</b>.</p>
            </BlueInfoBox>

            <Teleport to=".content">
                <div v-if="loading" class="StepLoader">
                    <div class="StepLoader__inner">
                        <div class="OfferLoader">
                            <svg width="91" height="107" viewBox="0 0 91 107" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M50.6218 3.5L6.08909 56.9393C4.34504 59.0321 3.47302 60.0785 3.4597 60.9623C3.44811 61.7306 3.79046 62.4615 4.38809 62.9445C5.07555 63.5 6.43769 63.5 9.16198 63.5H45.6218L40.6218 103.5L85.1545 50.0607C86.8986 47.9679 87.7706 46.9215 87.7839 46.0377C87.7955 45.2694 87.4531 44.5385 86.8555 44.0555C86.1681 43.5 84.8059 43.5 82.0816 43.5H45.6218L50.6218 3.5Z" stroke="#96ff00" stroke-width="6" stroke-linecap="round" stroke-linejoin="round" />
                            </svg>
                        </div>
                        <p><b>Generujemy dla Ciebie DWA dokumenty:<br>Umowę i Pełnomocnictwo</b></p>
                        <p>
                            PAMIĘTAJ! Podczas podpisywania dokumentów, musisz podpisać dwa dokumenty:<br>
                            Umowę na dostawę energii<br>
                            Pełnomocnictwo do zmiany sprzedawcy.
                        </p>
                    </div>
                </div>
            </Teleport>
        </StepBox>
    `,

    emits: [
        'next',
    ],

    props: {
        options: { type: Object as PropType<SmeOrderJson>, required: true },
        step: { type: Number, required: true },
    },

    data() {
        return {
            error: null,

            sent: false,
            loading: false,
        };
    },

    computed: {
        valid() {
            return this.options.valid;
        },

        promotitle() {
            return 'Pierwsze ' + this.options.offer.discount_period + ' miesięcy 1gr/kWh';
        },

        priceCashback() {
            return Utils.priceFormat(this.options.offer?.cashback, true, ' zł');
        },

        priceOffer1() {
            return Utils.priceFormat(this.options.offer.prices[0].price_netto, false, ' zł netto', 4);
        },

        priceOffer2() {
            return Utils.priceFormat(this.options.offer.prices[1].price_netto, false, ' zł netto', 4);
        },

        priceFee() {
            return Utils.priceFormat(this.options.fee_netto, false, ' zł');
        },

        usage() {
            return this.options.usage + ' kWh';
        },

        pricewarrantytitle() {
            return this.options.offer?.price_warranty ? Utils.yearsTitle(this.options.offer?.price_warranty) : null;
        },

        offerperiod() {
            return this.options.offer?.period ? Utils.yearsTitle(this.options.offer.period) : 'Czas nieoznaczony';
        },

        noticeperiod() {
            return Utils.monthsTitle(this.options.noticeperiod ?? 3);
        },

        contractterm() {
            const terms: OfferTerm[] = this.options.offer.terms_summary;

            if (!terms.length) {
                return null;
            }

            return terms[0];
        },
    },

    mounted() {
        if (this.options.captchakey) {
            Captcha.init(this.options.captchakey);
        }
    },

    methods: {
        async onNext() {
            try {
                this.error = null;
                this.loading = true;

                const token = this.options.captchakey ? await Captcha.submit(this.options.captchakey) : null;

                Tracking.doEvent('b2bsme_companydata_confirmation');

                const r = await Utils.setCart('form.summary', {
                    token: token,
                });

                this.sent = true;

                if (r.redirect) {
                    window.location.href = r.redirect;
                } else {
                    this.loading = false;
                }
            } catch (e) {
                this.error = e.message;
                this.loading = false;
            }
        },
    },
});
